import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PortalLoginPage = ({ branding, embedStyle, customerConfig, onLoginSuccess }) => {
  const primaryColor = branding?.primaryColor || '#107570';
  const radius = embedStyle?.borderRadius || '8px';

  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const validate = () => {
    const errs = {};
    if (!formData?.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) errs.email = 'Enter a valid email';
    if (!formData?.password) errs.password = 'Password is required';
    else if (formData?.password?.length < 8) errs.password = 'Password must be at least 8 characters';
    if (activeTab === 'register' && formData?.password !== formData?.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (authError) setAuthError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setAuthError('');
    try {
      if (activeTab === 'login') {
        const { error } = await supabase?.auth?.signInWithPassword({
          email: formData?.email,
          password: formData?.password,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase?.auth?.signUp({
          email: formData?.email,
          password: formData?.password,
        });
        if (error) throw error;
      }
      onLoginSuccess?.();
    } catch (err) {
      setAuthError(err?.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOLogin = async (provider) => {
    setSsoLoading(provider);
    try {
      const { error } = await supabase?.auth?.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location?.origin}/white-label-customer-portal`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setAuthError(err?.message || `${provider} sign-in failed.`);
      setSsoLoading('');
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (err) {
      setAuthError(err?.message || 'Failed to send reset email.');
    } finally {
      setForgotLoading(false);
    }
  };

  const inputBase = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${embedStyle?.borderColor || '#e2e8f0'}`,
    borderRadius: radius,
    fontSize: '14px',
    fontFamily: embedStyle?.fontFamily !== 'System Default' ? embedStyle?.fontFamily : undefined,
    outline: 'none',
    backgroundColor: '#fff',
    color: '#1a202c',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  const focusStyle = (e) => {
    e.target.style.borderColor = primaryColor;
    e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`;
  };
  const blurStyle = (e) => {
    e.target.style.borderColor = embedStyle?.borderColor || '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  const agencyInitial = (branding?.agencyName || 'A')?.charAt(0)?.toUpperCase();
  const portalName = customerConfig?.portalTitle || branding?.agencyName || 'Client Portal';

  return (
    <div
      className="min-h-screen flex"
      style={{ fontFamily: embedStyle?.fontFamily !== 'System Default' ? embedStyle?.fontFamily : undefined }}
    >
      {/* ── Left branding panel (hidden on mobile) ── */}
      <div
        className="hidden lg:flex lg:w-[420px] xl:w-[480px] flex-col justify-between p-10 flex-shrink-0"
        style={{ backgroundColor: primaryColor }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          {branding?.logoUrl ? (
            <img src={branding?.logoUrl} alt={branding?.agencyName} className="h-10 w-auto object-contain brightness-0 invert" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20 text-white text-lg font-bold">
              {agencyInitial}
            </div>
          )}
          <span className="text-white font-semibold text-lg">{portalName}</span>
        </div>

        {/* Hero text */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
            Your branded<br />forms portal
          </h2>
          <p className="text-white/70 text-sm leading-relaxed mb-8">
            Access all your forms, view submissions, and manage your account — all in one place, branded just for you.
          </p>

          {/* Feature list */}
          <div className="space-y-3">
            {[
              { icon: 'LayoutGrid', text: 'Access all your published forms' },
              { icon: 'Inbox', text: 'View and export form submissions' },
              { icon: 'Shield', text: 'Secure, private access' },
            ]?.map((item) => (
              <div key={item?.text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/20 flex-shrink-0">
                  <Icon name={item?.icon} size={14} color="white" />
                </div>
                <span className="text-white/80 text-sm">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 text-xs text-white/50">
          {customerConfig?.privacyUrl && (
            <a href={customerConfig?.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">
              Privacy
            </a>
          )}
          {customerConfig?.termsUrl && (
            <a href={customerConfig?.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">
              Terms
            </a>
          )}
          {customerConfig?.showPoweredBy !== false && (
            <span className="ml-auto">{branding?.emailFooterText || `Powered by ${branding?.agencyName || 'Your Agency'}`}</span>
          )}
        </div>
      </div>

      {/* ── Right auth panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10" style={{ backgroundColor: '#f8fafc' }}>
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          {branding?.logoUrl ? (
            <img src={branding?.logoUrl} alt={branding?.agencyName} className="h-9 w-auto object-contain" />
          ) : (
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-base font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              {agencyInitial}
            </div>
          )}
          <span className="font-semibold text-gray-900 text-base">{portalName}</span>
        </div>

        <div className="w-full max-w-md">
          {/* Card */}
          <div
            className="overflow-hidden"
            style={{
              backgroundColor: '#fff',
              border: `1px solid ${embedStyle?.borderColor || '#e2e8f0'}`,
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            }}
          >
            {/* Accent bar */}
            <div className="h-1 w-full" style={{ backgroundColor: primaryColor }} />

            <div className="p-6 sm:p-8">
              {/* Heading */}
              <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-900 mb-1">
                  {showForgot
                    ? 'Reset your password'
                    : activeTab === 'login' ?'Sign in to your portal' :'Create your account'}
                </h1>
                <p className="text-sm text-gray-500">
                  {showForgot
                    ? "Enter your email and we'll send you a reset link."
                    : activeTab === 'login'
                    ? `Access your forms on ${portalName}`
                    : 'Register to access your branded forms portal'}
                </p>
              </div>

              {/* Tabs (only when not in forgot flow) */}
              {!showForgot && (
                <div
                  className="flex mb-6 rounded-lg overflow-hidden"
                  style={{ border: `1px solid ${embedStyle?.borderColor || '#e2e8f0'}` }}
                >
                  {['login', 'register']?.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => { setActiveTab(tab); setErrors({}); setAuthError(''); }}
                      className="flex-1 py-2 text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: activeTab === tab ? primaryColor : 'transparent',
                        color: activeTab === tab ? '#fff' : '#6b7280',
                      }}
                    >
                      {tab === 'login' ? 'Sign In' : 'Register'}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Forgot Password Flow ── */}
              {showForgot ? (
                <div>
                  {forgotSent ? (
                    <div className="text-center py-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                        style={{ backgroundColor: `${primaryColor}15` }}
                      >
                        <Icon name="Mail" size={22} color={primaryColor} />
                      </div>
                      <p className="text-sm font-medium text-gray-800 mb-1">Check your inbox</p>
                      <p className="text-xs text-gray-500 mb-4">
                        We sent a password reset link to <strong>{forgotEmail}</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
                        className="text-sm font-medium"
                        style={{ color: primaryColor }}
                      >
                        Back to sign in
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      {authError && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{authError}</p>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e?.target?.value)}
                          placeholder="you@example.com"
                          style={inputBase}
                          onFocus={focusStyle}
                          onBlur={blurStyle}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="w-full py-2.5 text-sm font-medium text-white rounded-md transition-opacity hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: primaryColor, borderRadius: radius }}
                      >
                        {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForgot(false); setAuthError(''); }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Back to sign in
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <>
                  {/* Auth Error */}
                  {authError && (
                    <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-200">
                      <p className="text-xs text-red-700">{authError}</p>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        style={inputBase}
                        onFocus={focusStyle}
                        onBlur={blurStyle}
                      />
                      {errors?.email && <p className="text-xs text-red-600 mt-1">{errors?.email}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                      <input
                        type="password"
                        name="password"
                        value={formData?.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        style={inputBase}
                        onFocus={focusStyle}
                        onBlur={blurStyle}
                      />
                      {errors?.password && <p className="text-xs text-red-600 mt-1">{errors?.password}</p>}
                    </div>

                    {activeTab === 'register' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData?.confirmPassword}
                          onChange={handleChange}
                          placeholder="Re-enter your password"
                          style={inputBase}
                          onFocus={focusStyle}
                          onBlur={blurStyle}
                        />
                        {errors?.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors?.confirmPassword}</p>}
                      </div>
                    )}

                    {activeTab === 'login' && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => { setShowForgot(true); setAuthError(''); }}
                          className="text-xs font-medium transition-colors"
                          style={{ color: primaryColor }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-2.5 text-sm font-medium text-white rounded-md transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor, borderRadius: radius }}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: '#fff' }} />
                          {activeTab === 'login' ? 'Signing in…' : 'Creating account…'}
                        </>
                      ) : (
                        activeTab === 'login' ? 'Sign In' : 'Create Account'
                      )}
                    </button>
                  </form>

                  {/* SSO Divider */}
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" style={{ borderColor: embedStyle?.borderColor || '#e2e8f0' }} />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 text-xs text-gray-400 bg-white">Or continue with</span>
                    </div>
                  </div>

                  {/* SSO Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { provider: 'google', label: 'Google', icon: 'Chrome' },
                      { provider: 'azure', label: 'Microsoft', icon: 'Box' },
                    ]?.map(({ provider, label, icon }) => (
                      <button
                        key={provider}
                        type="button"
                        onClick={() => handleSSOLogin(provider)}
                        disabled={!!ssoLoading}
                        className="flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-gray-700 bg-white border rounded-md transition-colors hover:bg-gray-50 disabled:opacity-60"
                        style={{ borderColor: embedStyle?.borderColor || '#e2e8f0', borderRadius: radius }}
                      >
                        {ssoLoading === provider ? (
                          <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#d1d5db', borderTopColor: '#6b7280' }} />
                        ) : (
                          <Icon name={icon} size={16} />
                        )}
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer links */}
          <div className="mt-5 flex items-center justify-center gap-4 text-xs text-gray-400">
            {customerConfig?.privacyUrl && (
              <a href={customerConfig?.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Privacy Policy</a>
            )}
            {customerConfig?.termsUrl && (
              <a href={customerConfig?.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Terms of Service</a>
            )}
            {customerConfig?.supportEmail && (
              <a href={`mailto:${customerConfig?.supportEmail}`} className="hover:text-gray-600 transition-colors">Support</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalLoginPage;
